import { useState, useEffect, useCallback } from "react";
import { TagBadge } from "./TagBadge";
import { apiFetch } from "../api/client";

const THRESHOLD_KEY = "infohub_top_stories_threshold";
const TAGS_KEY = "infohub_custom_tags";
const SCHEDULE_KEY = "infohub_ai_schedule";
const FILTER_CONFIG_KEY = "infohub_filter_config";

export interface FilterConfig {
  showUnread: boolean;
  showScore: boolean;
  scoreThreshold: number;
}

export const DEFAULT_FILTER_CONFIG: FilterConfig = {
  showUnread: true,
  showScore: true,
  scoreThreshold: 80,
};

export function loadFilterConfig(): FilterConfig {
  try {
    const saved = localStorage.getItem(FILTER_CONFIG_KEY);
    return saved ? { ...DEFAULT_FILTER_CONFIG, ...JSON.parse(saved) } : DEFAULT_FILTER_CONFIG;
  } catch {
    return DEFAULT_FILTER_CONFIG;
  }
}

const DEFAULT_TAGS = ["NIS2", "DORA", "DSGVO", "CRA", "AI-Act", "eIDAS", "MiCA"];
const DEFAULT_SCHEDULE = ["12:00", "00:00"];

function loadJSON<T>(key: string, fallback: T): T {
  try {
    const saved = localStorage.getItem(key);
    return saved ? JSON.parse(saved) : fallback;
  } catch {
    return fallback;
  }
}

interface SettingsViewProps {
  onTagsChange?: (tags: string[]) => void;
  onEmailChange?: (email: string) => void;
  onFilterConfigChange?: (config: FilterConfig) => void;
}

export function SettingsView({ onTagsChange, onEmailChange, onFilterConfigChange }: SettingsViewProps) {
  // ─── Newsletter E-Mail ───
  const [email, setEmail] = useState("");
  const [emailLoading, setEmailLoading] = useState(true);
  const [emailSaving, setEmailSaving] = useState(false);
  const [emailSaved, setEmailSaved] = useState(false);

  useEffect(() => {
    apiFetch<{ email: string }>("/settings/newsletter-email")
      .then((data) => setEmail(data.email || ""))
      .catch(() => {})
      .finally(() => setEmailLoading(false));
  }, []);

  const saveEmail = useCallback(async () => {
    setEmailSaving(true);
    setEmailSaved(false);
    try {
      await apiFetch("/settings/newsletter-email", {
        method: "PUT",
        body: JSON.stringify({ email: email.trim() }),
      });
      setEmailSaved(true);
      onEmailChange?.(email.trim());
      setTimeout(() => setEmailSaved(false), 3000);
    } catch {
      alert("Fehler beim Speichern der E-Mail-Adresse.");
    } finally {
      setEmailSaving(false);
    }
  }, [email, onEmailChange]);

  // ─── Top Stories Schwellenwert ───
  const [threshold, setThreshold] = useState(() => {
    const saved = localStorage.getItem(THRESHOLD_KEY);
    return saved ? Number(saved) : 80;
  });

  useEffect(() => {
    localStorage.setItem(THRESHOLD_KEY, String(threshold));
  }, [threshold]);

  // ─── Filter-Konfiguration ───
  const [filterConfig, setFilterConfig] = useState<FilterConfig>(loadFilterConfig);

  const updateFilter = useCallback((patch: Partial<FilterConfig>) => {
    setFilterConfig((prev) => {
      const next = { ...prev, ...patch };
      localStorage.setItem(FILTER_CONFIG_KEY, JSON.stringify(next));
      onFilterConfigChange?.(next);
      return next;
    });
  }, [onFilterConfigChange]);

  // ─── Konfigurierbare Tags/Schlagwörter ───
  const [tags, setTags] = useState<string[]>(() => loadJSON(TAGS_KEY, DEFAULT_TAGS));
  const [newTag, setNewTag] = useState("");

  useEffect(() => {
    localStorage.setItem(TAGS_KEY, JSON.stringify(tags));
    onTagsChange?.(tags);
  }, [tags, onTagsChange]);

  const addTag = useCallback(() => {
    const tag = newTag.trim();
    if (tag && !tags.includes(tag)) {
      setTags((prev) => [...prev, tag]);
      setNewTag("");
    }
  }, [newTag, tags]);

  const removeTag = useCallback((tag: string) => {
    setTags((prev) => prev.filter((t) => t !== tag));
  }, []);

  // ─── KI-Laufzeiten ───
  const [schedule, setSchedule] = useState<string[]>(() => loadJSON(SCHEDULE_KEY, DEFAULT_SCHEDULE));
  const [newTime, setNewTime] = useState("12:00");

  useEffect(() => {
    localStorage.setItem(SCHEDULE_KEY, JSON.stringify(schedule));
  }, [schedule]);

  const addSchedule = useCallback(() => {
    if (newTime && !schedule.includes(newTime)) {
      setSchedule((prev) => [...prev, newTime].sort());
    }
  }, [newTime, schedule]);

  const removeSchedule = useCallback((time: string) => {
    setSchedule((prev) => prev.filter((t) => t !== time));
  }, []);

  // ─── Hilfetext ───
  const [helpText, setHelpText] = useState("");
  const [helpLoading, setHelpLoading] = useState(true);
  const [helpSaving, setHelpSaving] = useState(false);
  const [helpSaved, setHelpSaved] = useState(false);

  useEffect(() => {
    apiFetch<{ content: string }>("/settings/help-text")
      .then((data) => setHelpText(data.content))
      .catch(() => {})
      .finally(() => setHelpLoading(false));
  }, []);

  const saveHelpText = useCallback(async () => {
    setHelpSaving(true);
    setHelpSaved(false);
    try {
      await apiFetch("/settings/help-text", {
        method: "PUT",
        body: JSON.stringify({ content: helpText }),
      });
      setHelpSaved(true);
      setTimeout(() => setHelpSaved(false), 3000);
    } catch {
      alert("Fehler beim Speichern des Hilfetexts.");
    } finally {
      setHelpSaving(false);
    }
  }, [helpText]);

  // ─── KI manuell starten ───
  const [aiRunning, setAiRunning] = useState(false);
  const [aiRunResult, setAiRunResult] = useState<string | null>(null);

  const triggerAiRun = useCallback(async () => {
    setAiRunning(true);
    setAiRunResult(null);
    try {
      await apiFetch("/ai/trigger", { method: "POST" });
      setAiRunResult("KI-Verarbeitung wurde gestartet. Neue Zusammenfassungen erscheinen in wenigen Minuten.");
    } catch {
      setAiRunResult("KI-Verarbeitung wurde angestoßen. Prüfe den Status unter Statistik.");
    }
    setAiRunning(false);
  }, []);

  // ─── KI-Bilder generieren ───
  const [imgRunning, setImgRunning] = useState(false);
  const [imgRunResult, setImgRunResult] = useState<string | null>(null);

  const triggerImageGen = useCallback(async () => {
    setImgRunning(true);
    setImgRunResult(null);
    try {
      const res = await apiFetch<{ status: string; message: string; queued: number }>("/ai/trigger-images", { method: "POST" });
      setImgRunResult(res.message);
    } catch {
      setImgRunResult("Bildgenerierung wurde angestoßen. Prüfe den Status in den Logs.");
    }
    setImgRunning(false);
  }, []);

  return (
    <div>
      <h2 className="admin-page-title">Einstellungen</h2>

      {/* ─── Newsletter E-Mail-Adresse ─── */}
      <section className="card settings-section">
        <h3>Newsletter E-Mail-Adresse</h3>
        <p className="text-muted">
          Diese Adresse wird in der Topbar angezeigt, damit Absender wissen, wohin sie Newsletter schicken können.
          Wenn leer, wird die Adresse nicht angezeigt.
        </p>

        {emailLoading ? (
          <p className="loading-text">Lade...</p>
        ) : (
          <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
            <input
              type="email"
              placeholder="z.B. regulatorik@meine-firma.de"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={{ flex: 1, maxWidth: 400 }}
            />
            <button
              className="btn-primary btn-sm"
              onClick={saveEmail}
              disabled={emailSaving}
            >
              {emailSaving ? "Speichere..." : emailSaved ? "Gespeichert!" : "Speichern"}
            </button>
            {emailSaved && (
              <span style={{ fontSize: 13, color: "var(--success)", fontWeight: 500 }}>
                E-Mail-Adresse gespeichert.
              </span>
            )}
          </div>
        )}
      </section>

      {/* ─── Top Stories Schwellenwert ─── */}
      <section className="card settings-section">
        <h3>Top Stories Schwellenwert</h3>
        <p className="text-muted">Ab welchem Relevanz-Score werden Artikel in den "Top Stories" angezeigt?</p>
        <div className="settings-slider">
          <input
            type="range"
            min={0}
            max={100}
            value={threshold}
            onChange={(e) => setThreshold(Number(e.target.value))}
          />
          <span className="settings-slider-value">{threshold}</span>
        </div>
      </section>

      {/* ─── Filter-Konfiguration ─── */}
      <section className="card settings-section">
        <h3>Filter-Konfiguration</h3>
        <p className="text-muted">
          Welche Filter-Chips sollen auf der News-Seite angezeigt werden?
        </p>

        <div style={{ display: "flex", flexDirection: "column", gap: 14, marginTop: 8 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={filterConfig.showUnread}
              onChange={(e) => updateFilter({ showUnread: e.target.checked })}
            />
            <span style={{ fontWeight: 500 }}>„Ungelesen"-Filter anzeigen</span>
          </label>

          <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={filterConfig.showScore}
              onChange={(e) => updateFilter({ showScore: e.target.checked })}
            />
            <span style={{ fontWeight: 500 }}>Score-Filter anzeigen</span>
          </label>

          {filterConfig.showScore && (
            <div style={{ marginLeft: 28 }}>
              <p className="text-muted" style={{ margin: "0 0 8px" }}>
                Ab welchem Score sollen Artikel als „hochrelevant" gelten?
              </p>
              <div className="settings-slider">
                <input
                  type="range"
                  min={10}
                  max={100}
                  step={5}
                  value={filterConfig.scoreThreshold}
                  onChange={(e) => updateFilter({ scoreThreshold: Number(e.target.value) })}
                />
                <span className="settings-slider-value">{filterConfig.scoreThreshold}</span>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ─── Tags / Schlagwörter ─── */}
      <section className="card settings-section">
        <h3>Schlagwörter / Tags</h3>
        <p className="text-muted">
          Konfiguriere die Schlagwörter, nach denen im Feed gefiltert werden kann.
          Diese werden auch der KI als Kategorien vorgegeben und erscheinen als Themen-Kanäle in der Sidebar.
        </p>

        <div className="tag-config-list">
          {tags.map((tag) => (
            <div key={tag} className="tag-config-row">
              <TagBadge tag={tag} />
              <span style={{ flex: 1 }} />
              <button
                className="btn-icon"
                onClick={() => removeTag(tag)}
                title="Tag entfernen"
                style={{ color: "var(--danger)" }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
          ))}
        </div>

        <div className="tag-config-add">
          <input
            type="text"
            placeholder="Neues Schlagwort..."
            value={newTag}
            onChange={(e) => setNewTag(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addTag()}
            style={{ flex: 1 }}
          />
          <button className="btn-primary btn-sm" onClick={addTag}>
            Hinzufügen
          </button>
        </div>
      </section>

      {/* ─── KI-Laufzeiten ─── */}
      <section className="card settings-section">
        <h3>KI-Verarbeitungszeiten</h3>
        <p className="text-muted">
          Zu welchen Uhrzeiten soll die KI-Analyse laufen?
          Die KI analysiert neue Artikel und erstellt Zusammenfassungen.
        </p>

        <div className="schedule-list">
          {schedule.map((time) => (
            <div key={time} className="schedule-row">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" />
              </svg>
              <span style={{ flex: 1, fontWeight: 600 }}>{time} Uhr</span>
              <button
                className="btn-icon"
                onClick={() => removeSchedule(time)}
                title="Zeitpunkt entfernen"
                style={{ color: "var(--danger)" }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
          ))}
        </div>

        <div className="schedule-add">
          <input
            type="time"
            value={newTime}
            onChange={(e) => setNewTime(e.target.value)}
          />
          <button className="btn-primary btn-sm" onClick={addSchedule}>
            Zeitpunkt hinzufügen
          </button>
        </div>

        <div style={{ marginTop: 20, paddingTop: 16, borderTop: "1px solid var(--border-light)", display: "flex", flexDirection: "column", gap: 16 }}>
          {/* KI-Zusammenfassungen */}
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <button
                className="btn-primary"
                onClick={triggerAiRun}
                disabled={aiRunning}
                style={{ display: "flex", alignItems: "center", gap: 8 }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polygon points="5 3 19 12 5 21 5 3" />
                </svg>
                {aiRunning ? "KI läuft..." : "KI jetzt starten"}
              </button>
              <span className="text-muted" style={{ fontSize: 13 }}>
                Startet die KI-Analyse sofort für alle unverarbeiteten Artikel.
              </span>
            </div>
            {aiRunResult && (
              <p style={{ marginTop: 10, fontSize: 13, color: "var(--success)", fontWeight: 500 }}>
                {aiRunResult}
              </p>
            )}
          </div>

          {/* KI-Bildgenerierung */}
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <button
                className="btn-primary"
                onClick={triggerImageGen}
                disabled={imgRunning}
                style={{ display: "flex", alignItems: "center", gap: 8 }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="3" width="18" height="18" rx="2" />
                  <circle cx="8.5" cy="8.5" r="1.5" />
                  <path d="M21 15l-5-5L5 21" />
                </svg>
                {imgRunning ? "Bilder werden generiert..." : "KI-Bilder generieren"}
              </button>
              <span className="text-muted" style={{ fontSize: 13 }}>
                Generiert DALL-E Bilder für alle Artikel ohne Thumbnail.
              </span>
            </div>
            {imgRunResult && (
              <p style={{ marginTop: 10, fontSize: 13, color: "var(--success)", fontWeight: 500 }}>
                {imgRunResult}
              </p>
            )}
          </div>
        </div>
      </section>

      {/* ─── Hilfetext ─── */}
      <section className="card settings-section">
        <h3>Hilfetext (Info-Popup)</h3>
        <p className="text-muted">
          Der Hilfetext wird im Info-Popup angezeigt (Fragezeichen-Icon in der Topbar).
          Markdown-Syntax wird unterstützt (##, ###, -, **fett**).
        </p>

        {helpLoading ? (
          <p className="loading-text">Lade Hilfetext...</p>
        ) : (
          <>
            <textarea
              className="help-text-editor"
              value={helpText}
              onChange={(e) => setHelpText(e.target.value)}
              rows={16}
              placeholder="Hilfetext in Markdown eingeben..."
            />
            <div className="feed-form-actions" style={{ marginTop: 12 }}>
              <button
                className="btn-primary"
                onClick={saveHelpText}
                disabled={helpSaving}
              >
                {helpSaving ? "Speichere..." : helpSaved ? "Gespeichert!" : "Hilfetext speichern"}
              </button>
              {helpSaved && (
                <span style={{ fontSize: 13, color: "var(--success)", fontWeight: 500 }}>
                  Hilfetext wurde gespeichert.
                </span>
              )}
            </div>
          </>
        )}
      </section>
    </div>
  );
}
